import React from 'react';
import type { MenuItemProps as AntdMenuItemProps } from 'antd/lib/menu';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import { Link } from 'react-router-dom';
import Card from '../Card';
import ContentList from '../ContentList';
import Dropdown from '../Dropdown';
import Icon, { IconNameType } from '../Icon';
import Button from '../Button';
import { Menu, MenuItem } from '../Menu';
import theme from '../theme';

const StyledCard = styled(Card)<{ $offsetTop?: number }>`
  && {
    max-width: 100vw;
    width: 600px;
    box-shadow: ${theme.getElevation()};
  }
`;

type Options = {
  icon: IconNameType;
  label: string;
  onClick: AntdMenuItemProps['onClick'];
}[];

type AppHeaderOverlayProps = {
  items?: {
    AvatarComponent: React.ReactNode;
    MainComponent: React.ReactNode;
    date: Date | string;
    onItemClick: () => void;
    showBadge: boolean;
  }[];
  OverlayButton: React.ReactNode;
  overlayVisible: boolean;
  setOverlayVisible: (visible: boolean) => void;
  closeMobileMenu: () => void;
  emptyDescription: string;
  isLoading: boolean;
  subtitle?: React.ReactNode;
  title: React.ReactNode;
  options: Options;
  pageUrl: string;
};

const AppHeaderOverlay: React.FC<AppHeaderOverlayProps> = ({
  items = [],
  OverlayButton,
  overlayVisible,
  setOverlayVisible,
  closeMobileMenu,
  emptyDescription,
  isLoading,
  subtitle,
  title,
  options,
  pageUrl,
}) => {
  const { auth } = React.useContext(AuthContext);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const close = () => {
    setOverlayVisible(false);
    closeMobileMenu();
  };

  return (
    <Dropdown
      placement="bottomLeft"
      trigger={['click']}
      open={overlayVisible}
      onOpenChange={visible => setOverlayVisible(visible)}
      overlayStyle={{
        position: 'fixed',
      }}
      dropdownRender={() => (
        <div ref={dropdownRef}>
          <StyledCard
            $offsetTop={dropdownRef?.current?.offsetTop}
            title={title}
            type="secondary"
            subtitle={subtitle}
            bodyStyle={{
              maxHeight: `calc(100vh - ${
                (dropdownRef?.current?.offsetTop ?? 0) + 320
              }px)`,
              overflow: 'auto',
            }}
            extra={
              !auth?.assumerIdMlsStaff && (
                <Dropdown
                  arrow
                  placement="bottomRight"
                  dropdownRender={() => <OptionsMenu options={options} />}
                  getPopupContainer={el => el.parentElement!}
                >
                  <Button
                    icon={<Icon type="more" width={30} />}
                    shape="circle"
                    type="text"
                  />
                </Dropdown>
              )
            }
            actions={[
              <Link key="0" to={pageUrl} onClick={close}>
                See All
              </Link>,
            ]}
          >
            <ContentList
              items={items}
              isLoading={isLoading}
              emptyDescription={emptyDescription}
            />
          </StyledCard>
        </div>
      )}
      getPopupContainer={el => el.parentElement!}
    >
      <div ref={dropdownRef}>{OverlayButton}</div>
    </Dropdown>
  );
};

const OptionsMenu: React.FC<{
  options: Options;
}> = ({ options }) => (
  <Menu selectable={false}>
    {options.map(({ icon, label, onClick }, i: number) => (
      <MenuItem key={i} onClick={onClick} icon={<Icon type={icon} />}>
        {label}
      </MenuItem>
    ))}
  </Menu>
);

export default AppHeaderOverlay;
